/**
 * Student Plans (ENG-10000) — the per-student model behind the mockup page.
 *
 * ⚠️ NOTHING HERE IS STORED. See `components/hall-passes/StudentPlans.tsx` for
 * the full note; this module only holds the shapes and the pure functions that
 * turn them into sentences, so both can be unit tested without rendering the
 * page.
 *
 * ## Why one plan holds two opposite things
 *
 * A restriction and an accommodation are the same sentence with the sign
 * flipped: "this student may not", "this student always may". A school does not
 * hold them apart in its head, and the two are frequently written by the same
 * person in the same meeting. Splitting them into two pages would mean reading
 * two pages to answer "what is set up for this kid", which is the only question
 * this page exists to answer.
 *
 * What they are NOT is one list. They are read differently: a restriction is
 * checked when someone is refused, an accommodation when someone is let through.
 * So: one plan, two tables.
 */

// ---------------------------------------------------------------------------
// The model
// ---------------------------------------------------------------------------

/** How often a custom limit resets. Matches Set Pass Limits' three periods. */
export type LimitPeriod = 'day' | 'week' | 'month';

/**
 * What the limit counts, matching Set Pass Limits: a number of passes, or
 * total minutes out. A plan that could not express a minutes cap would show a
 * synced "90 minutes per day" row it then could not edit.
 */
export type LimitMetric = 'passes' | 'minutes';

/**
 * What a teacher may do when a restricted student asks anyway.
 *
 * Three answers rather than a boolean, because "may be overridden" and "may be
 * overridden silently" are very different promises to make to a family, and the
 * note is the whole record of why it happened.
 */
export type OverrideAbility = 'note' | 'no-note' | 'none';

/**
 * What every rule aimed at ONE student's pass types carries.
 *
 * Capping a pass type and closing it off are the same conversation with the
 * same follow-up questions — which types, who may override, what does the
 * teacher read, who has to approve — so they share a shape and differ only in
 * whether a number is part of the answer.
 */
interface PassTypeRuleBase {
  id: string;
  /** Pass types this rule covers, by NAME (the mock has no destination ids). */
  passTypeNames: string[];
  override: OverrideAbility;
  /** Shown to a teacher at the moment they are issuing. */
  description: string;
  requiresSupervision: boolean;
  supervisorIds: string[];
  /** Resolved at save time, so a row can name its people with no lookup. */
  supervisorNames: string[];
}

/** A capped pass type on a student's plan. The cap is always at least 1. */
export interface PassTypeRestriction extends PassTypeRuleBase {
  kind: 'pass-type';
  /**
   * Passes (or minutes) allowed per period. Floored at 1 by the editor: a cap
   * of nothing is not a small limit, it is a different rule, and it lives in
   * `IneligibleRestriction` where the override question is answered on its own
   * terms.
   */
  amount: number;
  metric: LimitMetric;
  period: LimitPeriod;
}

/**
 * "This student does not take this pass."
 *
 * Split out of the limit flavor rather than expressed as a limit of zero. A
 * blocked pass and a capped one ask different questions of a school: a cap is
 * a number to tune, a block is a door to close, and the emergency-override
 * machinery below only ever applied to the second. Folding them together meant
 * every ordinary limit dragged that machinery along, and every reader of a row
 * had to know that `0` was not arithmetic.
 */
export interface IneligibleRestriction extends PassTypeRuleBase {
  kind: 'ineligible';
}

/**
 * "These two cannot be out at the same time."
 *
 * Deliberately carries no limit, no override and no supervision: it is not a
 * cap on one student, it is a relationship between two. In the real build it is
 * backed by a No Meetup group, which is why the row wears that badge and why
 * the modal warns that the other student's plan changes too.
 */
export interface PeerRestriction {
  kind: 'peer';
  id: string;
  peerIds: string[];
  peerNames: string[];
}

export type Restriction = PassTypeRestriction | IneligibleRestriction | PeerRestriction;

/**
 * Which of the three a restriction is, on its own.
 *
 * Named because it travels ahead of the row itself: the Add Restriction menu
 * picks a flavor, and the editor opens on it before there is a `Restriction`
 * to speak of.
 */
export type RestrictionFlavor = Restriction['kind'];

/**
 * How long ONE of this student's passes may run before it counts as late.
 *
 * A different question from the budget below, and the distinction is the whole
 * point of the field. `amount`/`metric`/`period` cap how MUCH a student may be
 * out across a day or a week; this caps a SINGLE trip. A student can sit well
 * inside a generous weekly budget and still be marked overdue eight minutes
 * into every visit.
 *
 *  - `default` — the pass type's own Time limit stands.
 *  - `none`    — this student's covered passes never run out of time.
 *  - `custom`  — `timeLimitMinutes` instead of the pass type's.
 */
export type AccommodationTimeLimit = 'default' | 'none' | 'custom';

/** One "this student always may" rule. */
export interface Accommodation {
  id: string;
  passTypeNames: string[];
  /** The exemption case: no cap at all for the covered types. */
  unlimited: boolean;
  amount: number;
  metric: LimitMetric;
  period: LimitPeriod;
  /**
   * Per-pass time limit (ENG-9785 follow-on).
   *
   * This is the ONLY accommodation whose absence is PUBLIC. Every other rule
   * here fails quietly, in a conversation: a student is stopped from taking a
   * pass. Going over a time limit announces itself — the student's card turns
   * red on a /points grid that may be projected, and can chime.
   *
   * So a student with a documented reason to be out longer is not "late" at
   * the school's five minutes; flagging them is a FALSE POSITIVE, and the fix
   * for a false positive is not a quieter alarm. `none` maps onto a rule both
   * campus and the client already implement — a pass with no time limit never
   * goes overdue — so it needs no new concept downstream.
   */
  timeLimit: AccommodationTimeLimit;
  /** Minutes for one pass. Meaningful only when `timeLimit` is `custom`. */
  timeLimitMinutes: number;
  /**
   * Skips the queue. Named for what it DOES rather than "override", because a
   * school reading this row needs to know which walls come down and which do
   * not (No Meetup never does).
   */
  immediateAccess: boolean;
}

export interface StudentPlan {
  id: string;
  studentId: string;
  studentName: string;
  grade: string;
  restrictions: Restriction[];
  accommodations: Accommodation[];
  // A plan holds no schedules of its own. Its Scheduled Passes section is a
  // lens over `components/hall-passes/scheduledPassesStore.ts` filtered to this
  // student (`schedulesForStudent` in `lib/scheduledPasses.ts`). A second copy
  // stored here is exactly the drift this page exists to close: the two lists
  // would disagree the first time somebody edited one of them.
  /** ISO `YYYY-MM-DD`. */
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Counting
// ---------------------------------------------------------------------------

/**
 * Restrictions on a plan, PEER RULES INCLUDED.
 *
 * They are the half a school is most likely to forget it set, and a pill that
 * said "1 Restriction" over a plan holding a cap and two peer rules would be
 * the page's first lie.
 */
export function restrictionCount(plan: Pick<StudentPlan, 'restrictions'>): number {
  return plan.restrictions.length;
}

export function accommodationCount(plan: Pick<StudentPlan, 'accommodations'>): number {
  return plan.accommodations.length;
}

/** "1 Restriction" / "3 Restrictions". */
export function countLabel(n: number, singular: string): string {
  return `${n} ${n === 1 ? singular : `${singular}s`}`;
}

// ---------------------------------------------------------------------------
// Sentences
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Classifying a synced Pass Limits row
// ---------------------------------------------------------------------------

/** The grade- or school-level limit a student-scoped row is measured against. */
export interface BaselineLimit {
  amount: number;
  period: LimitPeriod;
  metric?: LimitMetric;
}

/** Passes per school day, so limits with different periods stay comparable. */
const PERIOD_DAYS: Record<LimitPeriod, number> = { day: 1, week: 5, month: 20 };

const dailyRate = (amount: number, period: LimitPeriod): number =>
  amount / PERIOD_DAYS[period];

/**
 * Which table a pre-existing student-scoped Pass Limits row lands in.
 *
 * The row itself is just a number; whether it is a restriction or an
 * accommodation is only knowable relative to what the student would get
 * WITHOUT it — the effective grade/school baseline for the same pass types:
 *
 * - `unlimited` is the standing exemption Set Pass Limits already supports,
 *   and is always an accommodation.
 * - Looser than the baseline grants extra: accommodation.
 * - Equal to or stricter than the baseline takes away: restriction. Equal is
 *   grouped with stricter because the row still pins the student — tightening
 *   the school-wide limit later would NOT tighten theirs.
 * - No baseline at all means the row constrains where nothing did: restriction.
 */
export function classifySyncedLimit(
  row: { unlimited?: boolean; amount: number; period: LimitPeriod; metric?: LimitMetric },
  baseline: BaselineLimit | null,
): 'restriction' | 'accommodation' {
  if (row.unlimited) return 'accommodation';
  if (!baseline) return 'restriction';
  // A passes cap and a minutes cap are not comparable rates; a row that caps a
  // dimension the baseline does not is constraining, whatever its number.
  if (row.metric && baseline.metric && row.metric !== baseline.metric) return 'restriction';
  return dailyRate(row.amount, row.period) > dailyRate(baseline.amount, baseline.period)
    ? 'accommodation'
    : 'restriction';
}

/**
 * MOCK: the Time limit each pass type carries, in minutes, keyed by name.
 *
 * The real build reads `auto_close_minutes` off the destination — the field the
 * pass type editor's **Time limit** writes. `null` is a type with no limit at
 * all, which is why the map is not simply absent-means-none.
 */
export const MOCK_PASS_TYPE_TIME_LIMITS: Record<string, number | null> = {
  Bathroom: 5,
  Nurse: 10,
  Counselor: 20,
  Library: 15,
  'Front Office': null,
};

/**
 * Is there a time limit here to override at all?
 *
 * A pass type with no limit can never make a student late, so an override for
 * one is a control that cannot do anything — worth hiding rather than
 * explaining. An empty selection covers ALL pass types, matching Set Pass
 * Limits, so it qualifies if any of them is limited.
 *
 * FAILS OPEN. An empty map means the destinations have not loaded, not that
 * nothing is limited, and hiding a real control because data was late is worse
 * than showing one that turns out inert.
 */
export function coveredTypesHaveTimeLimit(
  names: string[],
  limitByType: Record<string, number | null | undefined>,
): boolean {
  const known = Object.keys(limitByType);
  if (known.length === 0) return true;
  const pool = names.length === 0 ? known : names;
  return pool.some((name) => (limitByType[name] ?? null) !== null);
}

/**
 * MOCK: the school-level limits the plan modals compare a custom limit
 * against. The real build resolves the effective baseline for the selected
 * pass types from Set Pass Limits (school/grade rows) instead.
 */
export const MOCK_BASELINE_LIMITS: Record<string, BaselineLimit> = {
  Bathroom: { amount: 4, period: 'day', metric: 'passes' },
};

/**
 * The baseline a draft limit should be measured against, with the pass type
 * that carries it (so a warning can name it). An empty selection covers all
 * pass types, which necessarily includes every baselined one.
 */
export function baselineForTypes(
  names: string[],
): { passTypeName: string; baseline: BaselineLimit } | null {
  const pool = names.length === 0 ? Object.keys(MOCK_BASELINE_LIMITS) : names;
  for (const name of pool) {
    const baseline = MOCK_BASELINE_LIMITS[name];
    if (baseline) return { passTypeName: name, baseline };
  }
  return null;
}

/**
 * The one sentence a row reads when a student may not take a pass at all.
 *
 * Shared by the ineligible row's Limit cell and by `limitSentence`'s zero
 * guard, so the two can never drift into saying it differently.
 */
export const NO_PASSES_LABEL = 'No passes';

/**
 * A cap, in the words a school uses.
 *
 * A restriction row can no longer reach zero — that rule is now its own flavor
 * (`IneligibleRestriction`) — but the guard stays because the function is also
 * handed accommodation amounts and synced Pass Limits rows, and "0 passes per
 * day" is arithmetic where "No passes" is the answer.
 */
export function limitSentence(amount: number, period: LimitPeriod, metric: LimitMetric = 'passes'): string {
  if (amount <= 0) return NO_PASSES_LABEL;
  const unit = metric === 'minutes' ? 'minutes' : amount === 1 ? 'pass' : 'passes';
  return `${amount} ${unit} per ${period}`;
}

/** The accommodation column, where "no limit" is a real answer. */
export function accommodationLimitSentence(
  row: Pick<Accommodation, 'unlimited' | 'amount' | 'period' | 'metric'>,
): string {
  return row.unlimited ? 'No limit' : limitSentence(row.amount, row.period, row.metric);
}

/**
 * The per-pass time limit, in the words the pass type editor already uses.
 *
 * Returns NULL for `default` rather than a sentence: a row that does not touch
 * the time limit should say nothing about it, not "Pass type's limit". The
 * table has one line per rule as it is, and a column that always speaks turns
 * every accommodation into two facts to read instead of one.
 */
export function accommodationTimeLimitSentence(
  row: Pick<Accommodation, 'timeLimit' | 'timeLimitMinutes'>,
): string | null {
  if (row.timeLimit === 'none') return 'No time limit';
  if (row.timeLimit !== 'custom') return null;
  // Singular matters: "1 minutes per pass" in a plan a family may read is the
  // kind of thing that makes the whole document look unmaintained.
  const unit = row.timeLimitMinutes === 1 ? 'minute' : 'minutes';
  return `${row.timeLimitMinutes} ${unit} per pass`;
}

export const OVERRIDE_LABEL: Record<OverrideAbility, string> = {
  note: 'With a note',
  'no-note': 'Without a note',
  none: 'Not allowed',
};

/** Pass types on a row: names in full up to three, then a count. */
export function passTypeSummary(names: string[]): string {
  // Matches Set Pass Limits: a rule that names no pass types covers them all.
  if (names.length === 0) return 'All pass types';
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
}

/** People on a row (peers, supervising admins): same rule as pass types. */
export function nameSummary(names: string[]): string {
  if (names.length === 0) return 'Nobody selected';
  if (names.length <= 2) return names.join(', ');
  return `${names[0]} and ${names.length - 1} more`;
}

/** "Sep 2" — the same short form the scheduled-pass history uses. */
export function formatUpdated(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${MONTHS[m - 1]} ${d}`;
}

/** Two-letter initials for the list avatar. Falls back to one, then to "?". */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

// ---------------------------------------------------------------------------
// The bathroom case
// ---------------------------------------------------------------------------

const BATHROOM_PASS_TYPE = /bathroom|restroom/i;

/**
 * Does this rule close off a bathroom pass?
 *
 * Blocking a student from the bathroom is the one restriction on this page that
 * can go badly wrong in a way nothing else here can, and a school writing it is
 * almost never saying "never, under any circumstance" — they are saying "not on
 * request, not without an adult deciding". So the ineligible editor grays out
 * "Not allowed" and keeps an override on the rule.
 *
 * It takes no amount: the question only arises on the ineligible flavor now,
 * where there is no number to ask about. A CAPPED bathroom pass is an ordinary
 * limit and is left alone.
 *
 * Matched on the NAME because that is all a pass type carries here, and schools
 * name it both ways. Case-insensitive, and substring rather than equality, so
 * "Bathroom (upstairs)" and "Restroom pass" both catch.
 */
export function isEmergencyOverrideCase(passTypeNames: string[]): boolean {
  return passTypeNames.some((name) => BATHROOM_PASS_TYPE.test(name));
}

/** The selected name that made it the emergency case, for copy that names it. */
export function emergencyPassTypeName(passTypeNames: string[]): string | null {
  return passTypeNames.find((name) => BATHROOM_PASS_TYPE.test(name)) ?? null;
}

// ---------------------------------------------------------------------------
// Ids
// ---------------------------------------------------------------------------

let seq = 0;
/** Local-only id for a row this page just made. Never leaves the browser. */
export const newRowId = (prefix: string): string => `${prefix}-${Date.now()}-${seq++}`;
